// Décomposition prime nette d'une ligne de bordereau (émission / annulation) — MÊME logique que
// `decomposerItemCession` de CessionsPool.js et de la fonction SQL get_contrats_reglements_cession.
// L'assiette de rétrocession = PRIME NETTE (PN Pool + PN Compagnie), HORS accessoires + taxes.
// Les bordereaux agrégés ne portent que le TTC (`primeACeder`) ; on reconstruit la nette depuis le
// contrat embarqué (EmissionsDTO.contrat), prorata sur primeACeder / contrat.primeTotale.
export const decomposerItemCession = (it) => {
    const ttc = Number(it?.primeACeder) || 0;
    // 1) Décomposition à plat déjà fournie (source fiable).
    if (it && (it.primeNettePool != null || it.primeNetteCompagnie != null
        || it.accessoires != null || it.taxes != null)) {
        const pnc = Number(it.primeNetteCompagnie) || 0;
        const acc = Number(it.accessoires) || 0;
        const tax = Number(it.taxes) || 0;
        const pnp = it.primeNettePool != null ? Number(it.primeNettePool) : (ttc - pnc - acc - tax);
        return { primeNettePool: pnp, primeNetteCompagnie: pnc, accessoires: acc, taxes: tax, primeTTC: ttc };
    }
    // 2) Repli : décomposition depuis le contrat embarqué (prorata), sinon tout en PN Pool.
    const c = it?.contrat || {};
    const pt = Number(c.primeTotale) || 0;
    const ratio = pt !== 0 ? ttc / pt : 1;
    const pnc = (Number(c.primeNetteCompagnie) || 0) * ratio;
    const acc = (Number(c.accessoires) || 0) * ratio;
    const tax = (Number(c.taxes) || 0) * ratio;
    return {
        primeNettePool: ttc - pnc - acc - tax, // par soustraction ⇒ somme exacte = primeACeder
        primeNetteCompagnie: pnc,
        accessoires: acc,
        taxes: tax,
        primeTTC: ttc,
    };
};

// Construit la MAP de décomposition (prime nette par règlement) depuis le flux plat
// get_contrats_reglements_cession (`/api/contrat/organisme/month/cession/{code}`), clé id:{codeId}
// et repli np:{numeroPolice}|{montant}. Même appariement que /toutes-les-cessions.
export const construireMapDecomposition = (contrats) => {
    const map = {};
    (contrats || []).forEach((contrat) => {
        (contrat.reglements || []).forEach((reg) => {
            const parts = {
                primeNettePool: reg.primeNettePool,
                primeNetteCompagnie: reg.primeNetteCompagnie,
                accessoires: reg.accessoires,
                taxes: reg.taxes,
            };
            if (reg.codeId) map[`id:${reg.codeId}`] = parts;
            if (contrat.numeroPoliceP != null) map[`np:${contrat.numeroPoliceP}|${Number(reg.montant) || 0}`] = parts;
        });
    });
    return map;
};

// Vrai si une ligne de bordereau est APPARIÉE à la décomposition de la cession (= elle fait
// réellement partie du compte de cession officiel). Même clé que appliquerDecomposition.
export const estDansCession = (it, map) =>
    !!(map[`id:${it?.codeId}`] || map[`np:${it?.numeroPolice}|${Number(it?.primeACeder) || 0}`]);

// Injecte la décomposition prime nette sur chaque item de bordereau (apparié par code_id, repli
// police|montant). Les items agrégés ne portent que le TTC (primeACeder) sans décomposition.
// Une ligne qui porte DÉJÀ sa prime nette pool (fonction rétro get_cessions_for_retro_wr…, même calcul
// que le compte de cession) la GARDE : la décompo de PRÉPARATION (date-based + Volet A) l'écrasait avec
// une autre valeur ⇒ émission retro ≠ souscriptions cédées (MOTO août 2026 : 11 451 087 au lieu de
// 10 325 857, tableau de conformité NON CONFORME de −956 445).
export const appliquerDecomposition = (items, map) => (items || []).map((it) => {
    if (it?.primeNettePool != null) return it;
    const parts = map[`id:${it?.codeId}`] || map[`np:${it?.numeroPolice}|${Number(it?.primeACeder) || 0}`];
    return parts ? { ...it, ...parts } : it;
});

// RECONDUCTION FIDÈLE DE LA CESSION : ne garder que les lignes présentes dans la décomposition de
// la cession (= mêmes lignes que le compte de cession). Écarte les règlements que la fonction de
// rétrocession capte par sa 3ᵉ branche (encaissés dans le mois mais NON rattachés à la cession :
// cession_code_id & date_cession_pool NULL) — sinon la rétro sur-compte (au TTC, faute de
// décomposition) et diverge du compte de cession. Neutre si la map est vide (décompo indisponible).
export const filtrerLignesDansCession = (items, map) => {
    if (!map || !Object.keys(map).length) return items || [];
    // Une ligne qui porte DÉJÀ sa prime nette pool (fournie par la fonction rétro Option R) est
    // légitime en soi : on ne la drope jamais, même si la décompo de cession (autre sélection) ne
    // la contient pas. Sinon on filtre sur la décomposition de cession.
    return (items || []).filter((it) => it?.primeNettePool != null || estDansCession(it, map));
};

// Prime nette CÉDÉE d'une ligne = PN Pool UNIQUEMENT (la part compagnie, en co-assurance, est
// RETENUE par la compagnie et n'est PAS cédée au Pool). En TPV pur, PN Compagnie = 0 ⇒ pool = net total.
export const primeNetteDe = (it) => {
    const d = decomposerItemCession(it);
    return Number(d.primeNettePool) || 0;
};

// Arrondit chaque quote-part au franc et reporte le résidu (base − Σ) sur la plus grosse part
// (en valeur absolue) ⇒ Σ des montants = prime nette EXACTEMENT (miroir de ajusterArrondiAuFranc backend).
const ajusterArrondiAuFranc = (reps, base) => {
    if (!reps || !reps.length) return reps;
    const baseFranc = Math.round(base);
    let somme = 0;
    let idxMax = 0;
    reps.forEach((r, i) => {
        const m = Math.round(Number(r.montant) || 0);
        r.montant = m;
        somme += m;
        if (Math.abs(m) > Math.abs(Number(reps[idxMax].montant) || 0)) idxMax = i;
    });
    const residu = baseFranc - somme;
    if (residu !== 0 && reps[idxMax]) reps[idxMax].montant += residu;
    return reps;
};

// Réassoit une ligne de bordereau (émission/annulation) sur la PRIME NETTE :
//   - montantOriginal / montantRetrocede = prime nette (au lieu du TTC `totalRetrocede`) ;
//   - répartition :
//       • si `tauxList` fourni ⇒ RECOMPOSÉE = prime nette × taux (SOURCE DE VÉRITÉ = les taux de
//         l'exercice) — indépendante de la répartition STOCKÉE par règlement (souvent vide sur les
//         reprises, ou à montant 0 quand contrat.prime_nette = 0). Résout les 2 cas résiduels
//         « Taux 0 % / Montant 0 » et « Taux OK mais Montant 0 ».
//       • sinon (repli) ⇒ mise à l'échelle proportionnelle de la répartition stockée (× nette/TTC).
// La ligne conserve son totalRetrocede TTC d'origine sous `totalRetrocedeTTC` (traçabilité).
export const reassoirLignePrimeNette = (item, tauxList = null) => {
    const nette = primeNetteDe(item);
    const stored = (item?.repartition || []).filter((r) => (Number(r?.taux) || 0) > 0);
    let repartition;
    if (stored.length) {
        // 1) La ligne porte SA propre répartition (taux corrects) ⇒ montant = prime nette × son taux.
        //    (On préserve le taux de chaque ligne — indispensable si catégories mixtes M/MO/etc.)
        repartition = stored.map((r) => ({ ...r, montant: (nette / 100) * (Number(r.taux) || 0) }));
        ajusterArrondiAuFranc(repartition, nette);
    } else if (tauxList && tauxList.length) {
        // 2) Répartition STOCKÉE vide (reprise / prime 0) ⇒ recompose depuis les taux de l'exercice.
        repartition = tauxList.map((t) => ({
            compagnieCode: t.compagnieCode,
            compagnieNom: t.compagnieNom,
            taux: Number(t.taux) || 0,
            montant: (nette / 100) * (Number(t.taux) || 0),
        }));
        ajusterArrondiAuFranc(repartition, nette);
    } else {
        // 3) Dernier repli : mise à l'échelle proportionnelle du stocké (× nette/TTC).
        const brut = Number(item?.totalRetrocede) || 0;
        const ratio = brut !== 0 ? nette / brut : 1;
        repartition = (item?.repartition || []).map((r) => ({
            ...r,
            montant: (Number(r?.montant) || 0) * ratio,
        }));
    }
    return {
        ...item,
        repartition,
        totalRetrocedeTTC: item?.totalRetrocede,
        totalRetrocede: nette,
        montantRetrocede: nette,
    };
};

// Réassoit une ligne SINISTRE PAYÉ / RECOURS ENCAISSÉ sur sa répartition de rétrocession.
// ⚠️ Contrairement aux émissions, le taux d'un sinistre dépend de son EXERCICE DE SURVENANCE
// (date_sinistre) — il n'est donc PAS uniforme d'une ligne à l'autre. L'assiette est le montant
// du sinistre (montant_global), pas la prime nette (aucune décomposition accessoires/taxes ici).
//   - Si la répartition STOCKÉE (repartition_retrocession) est déjà renseignée (taux > 0) ⇒ on la
//     garde telle quelle (calculée au bon exercice à la rétrocession) : aucune régression.
//   - Sinon ⇒ on recompose depuis les clés de l'EXERCICE DE SURVENANCE de la ligne :
//     tauxParExercice[année(date_sinistre)] → montant = montant × taux, arrondi au franc.
//   - Si l'exercice est introuvable (date 'N/A' ou pas de clés pour l'année) ⇒ répartition vide (0),
//     plutôt que d'appliquer un taux erroné.
export const reassoirLigneSinistreRetro = (item, tauxParExercice = null) => {
    const montant = Number(item?.montant) || 0;
    const stored = (item?.repartition || []).filter((r) => (Number(r?.taux) || 0) > 0);
    if (stored.length) {
        // Répartition déjà stockée au bon exercice → on la conserve intacte.
        return { ...item, montantRetrocede: Number(item?.totalRetrocede) || 0 };
    }
    const annee = String(item?.dateSinistre || "").slice(0, 4);
    const tauxList = (/^\d{4}$/.test(annee) && tauxParExercice) ? tauxParExercice[annee] : null;
    if (!tauxList || !tauxList.length) {
        // Exercice de survenance non résolu → on laisse à 0 (pas de taux inventé).
        return { ...item, repartition: [], totalRetrocede: 0, montantRetrocede: 0 };
    }
    const repartition = tauxList.map((t) => ({
        compagnieCode: t.compagnieCode,
        compagnieNom: t.compagnieNom,
        taux: Number(t.taux) || 0,
        montant: (montant / 100) * (Number(t.taux) || 0),
    }));
    ajusterArrondiAuFranc(repartition, montant);
    return { ...item, repartition, totalRetrocede: montant, montantRetrocede: montant };
};
